
/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { Grid, IconButton } from '@mui/material';
import Datagrid from '../../Datagrid';
import PropTypes from 'prop-types';
import { DeleteRounded, SaveRounded } from '@mui/icons-material';
import columns from './table_config';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import SelectDrillDetails from '../../SelectDrillDetails';
import { createRollover, deleteRollover, updateRollover } from '../../../../store/actions/hydraulicsRollover';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { checkPermissions } from '../../../../utils/checkPermissions';
import { APP_ACTION_TYPES } from '../../../../utils/constants';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

const RolloverModal = ({
  selectedJob,
  rowData,
  proxyLoader,
  setAccordionProperty,
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { drillDetails, mudPumps, details } = useSelector((state) => state.hydraulics);
  const rollover = useSelector((state) => state.rollover);
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();

  useEffect(() => {
    if (!rollover.loading && !drillDetails?.loading && !details?.loading && !mudPumps?.loading) {
      const { drillFluids = [] } = drillDetails?.hydraulicsDrillDetails || {};
      const { data: rolloverDetails = [] } = rollover;

      if (rolloverDetails.length > 0) {
        const runFluidId = details?.data?.[0]?.drillFluidsId;
        const runDetailsFluid = drillFluids.find(item => item._id === runFluidId);
        const checkRunFluidIdMatch = rollover?.data?.[0]?.fromFluidId === runFluidId;

        const formatFluid = (fluid) =>
        `${fluid.name.trim()} @ ${fluid.MD.toFixed(drillDetails?.units?.MD?.decimals)}${drillDetails?.units?.MD?.label}`;

        const mudpumpsData = mudPumps?.data || [];
        const hasMudpumps = mudpumpsData.length > 0;

        const pumps_output = hasMudpumps
          ? Number(mudpumpsData[0]?.bbl_stk || 0)
          : '';

        const pumps_spm = hasMudpumps
          ? mudpumpsData.reduce((sum, pump) => {
            const spm = Number(pump?.spm ?? 0);
            return sum + (Number.isFinite(spm) ? spm : 0);
          }, 0)
          : '';

        const updatedRolloverDetails = rolloverDetails.map((detail) => {
          const drillFluidFrom = drillFluids.find(
            (item) => item._id === detail.fromFluidId
          );
          const drillFluidTo = drillFluids.find(
            (item) => item._id === detail.toFluidId
          );

          if (!drillFluidTo) {
            setInvalidRows(prev => ({ ...prev, [detail._id]: true }));
            dispatch(
              enqueueSnackbar(
                'Configured Drillfluid in Rollover is not found, Please Check',
                'error',
                new Date().getTime() + Math.random()
              )
            );
          }

          const updatedDetail = {
            ...detail,
            fromFluidId: drillFluidFrom ? formatFluid(drillFluidFrom) : 'N/A',
            toFluidId: drillFluidTo
              ? formatFluid(drillFluidTo)
              : '',
          };

          if (hasMudpumps) {
            updatedDetail.pumps_output = detail.pumps_output
              ? detail.pumps_output
              : pumps_output;

            updatedDetail.pumps_spm = detail.pumps_spm
              ? detail.pumps_spm
              : pumps_spm;
          }

          if (!drillFluidFrom || !drillFluidTo) {
            updatedDetail.isDirty = true;
          }

          if (!checkRunFluidIdMatch || !drillFluidFrom) {
            updatedDetail.fromFluidId = runDetailsFluid
              ? formatFluid(runDetailsFluid)
              : 'N/A';
            updatedDetail.isDirty = true;
          }

          return updatedDetail;
        });

        const shouldPreserveOld = properties?.some(item => item.isDirty) && checkRunFluidIdMatch;

        const finalProperties = shouldPreserveOld ? properties : updatedRolloverDetails;
        setProperties(finalProperties);
        setAccordionProperty(finalProperties);
      } else {
        handleAddProperty();
      }
    }
  }, [rollover.data, details]);

  useEffect(() => {
    if (properties[0]?.toFluidId === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'toFluidId', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'toFluidId');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddProperty = () => {
    const { drillFluids = [] } = drillDetails?.hydraulicsDrillDetails || {};
    const drillFluidFrom = drillFluids.find(
      item => item._id === details?.data?.[0]?.drillFluidsId
    );

    const mudpumpsData = mudPumps?.data || [];
    const hasMudpumps = mudpumpsData.length > 0;

    const pumps_output = hasMudpumps
      ? Number(mudpumpsData[0]?.bbl_stk || 0)
      : '';

    const pumps_spm = hasMudpumps
      ? mudpumpsData.reduce((sum, pump) => {
        const spm = Number(pump?.spm ?? 0);
        return sum + (Number.isFinite(spm) ? spm : 0);
      }, 0)
      : '';

    const newRow = {
      fromFluidId: drillFluidFrom
        ? `${drillFluidFrom?.name?.trim()} @ ${drillFluidFrom?.MD?.toFixed(
          drillDetails?.units?.MD?.decimals
        )}${drillDetails?.units?.MD?.label}`
        : 'N/A',
      toFluidId: '',
      pumps_output,
      pumps_spm,
      grid_id: 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
    };

    setProperties([newRow]);
    setAccordionProperty([newRow]);
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: value,
            isDirty,
          };
        }
        return item;
      });
      setAccordionProperty(updatedProperties);
      return updatedProperties;
    });
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateRolloverData = (row) => {
    const { pumps_output, pumps_spm, _id } = row;
    const min_pumps_output = rollover?.units?.pumps_output?.min;
    const max_pumps_output = rollover?.units?.pumps_output?.max;
    const min_pumps_spm = rollover?.units?.pumps_spm?.min;
    const max_pumps_spm = rollover?.units?.pumps_spm?.max;
    if (pumps_output !== '' && !isNaN(pumps_output)) {
      const value = Number(pumps_output);
      if (value <= min_pumps_output || value > max_pumps_output) {
        return showError(
        `Pumps Output should be greater than ${min_pumps_output} and less than ${max_pumps_output}`,
        _id
        );
      }
    }
    if (pumps_spm !== '' && !isNaN(pumps_spm)) {
      const value = Number(pumps_spm);
      if (value <= min_pumps_spm || value > max_pumps_spm) {
        return showError(
        `Pumps SPM should be greater than ${min_pumps_spm} and less than ${max_pumps_spm}`,
        _id
        );
      }
    }
    return true;
  };

  const checkRolloverValues = (row) => {
    const invalidFields = [];
    if (!row.fromFluidId) invalidFields.push('From Mud Fluid');
    if (!row.toFluidId) invalidFields.push('To MudFluid');
    if (!row.pumps_output) invalidFields.push('Pumps Output');
    if (!row.pumps_spm) invalidFields.push('Total SPM');

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateRolloverData(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const checkFromToFluidId = (row) => {
    const fromId = drillDetails?.hydraulicsDrillDetails?.drillFluids.filter((item) => `${item?.name?.trim()} @ ${item?.MD.toFixed(drillDetails?.units?.MD?.decimals)}${drillDetails?.units?.MD?.label}` === row?.fromFluidId)[0]._id;
    const toId = drillDetails?.hydraulicsDrillDetails?.drillFluids.filter((item) => `${item?.name?.trim()} @ ${item?.MD.toFixed(drillDetails?.units?.MD?.decimals)}${drillDetails?.units?.MD?.label}` === row?.toFluidId)[0]._id;
    if (fromId === toId) {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          'From Mud Fluid and To Mud Fluid Should not be the same',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    }
  };

  const checkFlowRate = (row) => {
    if (details?.data[0]?.flow_in >= 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          'Flow rate cannot be negative number',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveProperty = (row) => {
    if (checkRolloverValues(row) && checkFromToFluidId(row) && checkFlowRate(row)) {
      const payload = {
        fromFluidId: drillDetails?.hydraulicsDrillDetails?.drillFluids.filter((item) => `${item?.name?.trim()} @ ${item?.MD.toFixed(drillDetails?.units?.MD?.decimals)}${drillDetails?.units?.MD?.label}` === row?.fromFluidId)[0]._id,
        toFluidId: drillDetails?.hydraulicsDrillDetails?.drillFluids.filter((item) => `${item?.name?.trim()} @ ${item?.MD.toFixed(drillDetails?.units?.MD?.decimals)}${drillDetails?.units?.MD?.label}` === row?.toFluidId)[0]._id,
        wellsInfoId: rowData?.wellsInfoId,
        runId: rowData?._id,
        pumps_output: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row?.pumps_output, rollover?.units?.pumps_output?.conversionFactor) : Number(row?.pumps_output),
        pumps_spm: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row?.pumps_spm, rollover?.units?.pumps_spm?.conversionFactor) : Number(row?.pumps_spm),
      };
      if (row?.isNewRow) {
        dispatch(createRollover(payload));
      } else {
        const hydraulicsBody = {
          isRolloverResults: rowData?.isRolloverResults,
          query: selectedJob?.fromWells
            ? {
                wellsInfoId: selectedJob?.wellsInfoId
              }
            : {
                jobId: selectedJob?._id
              },
        };
        dispatch(updateRollover(row, payload, hydraulicsBody));
      }
      const updateIsDirtyFalse = (list) =>
        list.map((item) =>
          item._id === row._id ? { ...item, isDirty: false } : item
        );
      setProperties((prev) => updateIsDirtyFalse(prev));
      setAccordionProperty((prev) => updateIsDirtyFalse(prev));
    }
  };

  const handleDelete = (row) => {
    const body = {
      _id: row?._id,
      runId: rowData?._id,
      wellsInfoId: rowData?.wellsInfoId,
      isRolloverResults: rowData?.isRolloverResults,
      query: selectedJob?.fromWells
        ? {
            wellsInfoId: selectedJob?.wellsInfoId
          }
        : {
            jobId: selectedJob?._id
          }
    };
    dispatch(deleteRollover(body));
    setDeleteAction(false);
    setDeleteRow({});
    setProperties([]);
    setAccordionProperty([]);
  };

  const renderActionCell = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || row?.fromFluidId === 'N/A' || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, rollover?.permissions))}
          onClick={() => {
            saveProperty(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={row?.isNewRow || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, rollover?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderDrillFluidCell = (params) => {
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={drillDetails?.hydraulicsDrillDetails?.drillFluids}
        label={drillDetails?.units?.MD?.label}
        decimals={drillDetails?.units?.MD?.decimals}
      />
    );
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <>
      <Grid container maxWidth='1000px'>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            loading={rollover.loading || details?.loading}
            getRowId={(row) => row._id}
            columns={columns(renderActionCell, renderDrillFluidCell, renderValueCell, unit, rollover?.units)}
            disableStickyHeader={false}
            autoHeight={false}
            height={'150px'}
            sx={{
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900,
                pointerEvents: proxyLoader ? 'none' : 'auto',
                backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                minHeight: '0px'
              },
              '& .Mui-disabled': {
                opacity: 0.5,
              }
            }}
            getRowClassName={(params) =>
              proxyLoader ? 'Mui-disabled' : (invalidRows[params.id] ? classes.invalidRow : '')
            }
            isRowSelectable={() => !proxyLoader}
            hideFooterPagination
            hideFooterSelectedRowCount
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            permissions={rollover?.permissions}
          />
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={handleDelete}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      </>
  );
};
RolloverModal.propTypes = {
  selectedJob: PropTypes.object,
  rowData: PropTypes.object,
  setAccordionProperty: PropTypes.func,
  proxyLoader: PropTypes.bool
};
RolloverModal.defaultProps = {
  selectedJob: {}
};

export default RolloverModal;
